import { useState } from 'react';
import {
  AppDateField,
  AppFormDialog,
  AppMenuSheet,
  AppPickerRow,
  AppTextField,
  type IconName,
} from '@core/designsystem';
import type { EventType } from '@core/domain/model';

/**
 * Новое событие: заседание, встреча, задача или просто событие.
 *
 * Диалог живёт в `core/ui`, потому что событие заводят из двух мест: из календаря
 * на выбранный день и **из карточки дела** — заседание назначают, глядя в дело,
 * а не открывая календарь ([D-36](docs/DECISIONS.md), так же на Android). Раздел
 * разделу не сосед (правило 1 `check-rules.mjs`), поэтому общее лежит здесь.
 */

const TYPE_LABEL: Record<EventType, string> = {
  Hearing: 'Заседание',
  Meeting: 'Встреча',
  Task: 'Задача',
  Other: 'Событие',
};

const TYPE_ICON: Record<EventType, IconName> = {
  Hearing: 'gavel',
  Meeting: 'group',
  Task: 'checklist',
  Other: 'event',
};

const TYPES: EventType[] = ['Hearing', 'Meeting', 'Task', 'Other'];

export const eventTypeLabel = (type: EventType): string => TYPE_LABEL[type];
export const eventTypeIcon = (type: EventType): IconName => TYPE_ICON[type];

export interface EventDraft {
  title: string;
  type: EventType;
  startAt: number;
  caseId: string | null;
}

export function NewEventDialog({
  date,
  cases,
  fixedCaseId,
  onDismiss,
  onCreate,
}: {
  date: number;
  cases: { id: string; title: string }[];
  /**
   * Дело известно заранее: событие заводят внутри него, и выбирать нечего.
   * Строка «Дело» тогда не показывается вовсе — она была бы вопросом с одним ответом.
   */
  fixedCaseId?: string;
  onDismiss: () => void;
  onCreate: (draft: EventDraft) => void;
}) {
  const at = new Date(date);
  const pad = (value: number) => String(value).padStart(2, '0');

  const [title, setTitle] = useState('');
  const [type, setType] = useState<EventType>('Hearing');
  const [day, setDay] = useState(
    `${at.getFullYear()}-${pad(at.getMonth() + 1)}-${pad(at.getDate())}`,
  );
  const [clock, setClock] = useState('10:00');
  const [caseId, setCaseId] = useState<string | null>(fixedCaseId ?? null);
  const [picking, setPicking] = useState<'type' | 'case' | null>(null);

  const chosen = cases.find((item) => item.id === caseId);

  return (
    <>
      <AppFormDialog
        title="Новое событие"
        onDismiss={onDismiss}
        confirm={{
          label: 'Создать',
          enabled: title.trim().length > 0,
          onClick: () => {
            const moment = new Date(`${day}T${clock}`);
            if (!Number.isNaN(moment.getTime())) {
              onCreate({ title, type, startAt: moment.getTime(), caseId });
            }
          },
        }}
      >
        <AppTextField value={title} onChange={setTitle} label="Что назначено" autoFocus />
        <AppPickerRow label="Тип" value={eventTypeLabel(type)} onClick={() => setPicking('type')} />
        <AppDateField value={day} onChange={setDay} label="Дата" />
        <AppTextField value={clock} onChange={setClock} label="Время" />
        {fixedCaseId ? null : (
          <AppPickerRow
            label="Дело"
            value={chosen?.title ?? 'не выбрано'}
            onClick={() => setPicking('case')}
          />
        )}
        {/*
         * Напоминания здесь нет намеренно: на iOS оно возможно только пушем с нашего
         * сервера, и это этап 7. Обещать в интерфейсе то, чего нет, нельзя.
         */}
      </AppFormDialog>

      {picking === 'type' ? (
        <AppMenuSheet
          title="Тип события"
          onDismiss={() => setPicking(null)}
          items={TYPES.map((item) => ({
            icon: TYPE_ICON[item],
            label: eventTypeLabel(item),
            onSelect: () => setType(item),
          }))}
        />
      ) : null}

      {picking === 'case' ? (
        <AppMenuSheet
          title="Дело события"
          onDismiss={() => setPicking(null)}
          items={[
            { icon: 'close', label: 'Без дела', onSelect: () => setCaseId(null) },
            ...cases.map((item) => ({
              icon: 'folder' as const,
              label: item.title,
              onSelect: () => setCaseId(item.id),
            })),
          ]}
        />
      ) : null}
    </>
  );
}
